import { BehaviorSubject, Observable } from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";
import { SubscriptionBase } from "@core/base/subscription-base";
import { ISubscription } from "@core/base/subscription-base.types";
import { IR2Copilot, IR2GetCopilotsResponse, IR2SellcodeApplyResponse, R2SellcodeApplyResult } from "@common/types/api/r2-types";
import { OfficeChatRepository } from "@core/office/office-chat-repository/office-chat-repository";
import { logger } from "@services/logger/logger.service";
import { SessionCryptoService } from "@services/session-crypto/session-crypto.service";

interface IR2UserSubscriptionResponse
{
    result: number;
    title?: string;
    user_name?: string;
    dt_from?: number;
    dt_to?: number;
    tokens_left?: number;
    tokens_used?: number;
}

const subscriptionDefault: ISubscription = {
    success: false,
    sessionId: '',
    tenantId: '',
    userId: '',
    userEmail: '',
    userHint: '',
    tokensLeft: 0,
    tokensTill: '',
    subscriptionsCount: 0,
};

export class OfficeSubscription implements SubscriptionBase
{
    private subscription: BehaviorSubject<ISubscription> = new BehaviorSubject<ISubscription>(subscriptionDefault);
    private copilots$: BehaviorSubject<IR2Copilot[]> = new BehaviorSubject<IR2Copilot[]>([]);
    private activeCopilot$: BehaviorSubject<IR2Copilot | null> = new BehaviorSubject<IR2Copilot | null>(null);
    private copilotsLoaded = false;
    private resetGeneration = 0;

    constructor(
        private chatRepository: OfficeChatRepository,
        private sessionCrypto: SessionCryptoService,
    ) { }

    public getCopilots(): IR2Copilot[]
    {
        return this.copilots$.getValue();
    }

    public $getCopilots(): Observable<IR2Copilot[]>
    {
        return this.copilots$.asObservable();
    }

    public getActiveCopilot(): IR2Copilot | null
    {
        return this.activeCopilot$.getValue();
    }

    public $getActiveCopilot(): Observable<IR2Copilot | null>
    {
        return this.activeCopilot$.asObservable();
    }

    public setActiveCopilot(copilot: IR2Copilot): void
    {
        this.activeCopilot$.next(copilot);
    }

    /**
     * Apply a promocode (sell code) to the active copilot's subscription.
     *
     * The backend maps non-success result codes to HTTP errors and echoes the
     * upstream code in the error body, so failures are read back from the rejected
     * response. Refreshes subscription info on success.
     */
    public async applySellcode(sellcode: string): Promise<R2SellcodeApplyResult>
    {
        const copilot = this.activeCopilot$.getValue();
        if (!copilot)
        {
            return R2SellcodeApplyResult.FailInvalidParameters;
        }

        try
        {
            const res = await this.sessionCrypto.postAddin<unknown, IR2SellcodeApplyResponse>(
                '/sellcode/apply',
                { copilot_id: copilot.id, sellcode },
            );

            if (res.result === R2SellcodeApplyResult.Success)
            {
                await this.fetchSubscription(this.subscription.getValue().sessionId);
            }

            return res.result;
        }
        catch (err)
        {
            const result = (err as HttpErrorResponse)?.error?.details?.result;
            return typeof result === 'number'
                ? result
                : R2SellcodeApplyResult.FailServiceTemporaryNotValid;
        }
    }

    public openSubscriptionDocs(): void
    {
        logger.warn('openSubscriptionDocs: not supported on the new backend yet — skipping');
    }

    public getSubscription(): ISubscription
    {
        return this.subscription.getValue();
    }

    public $getSubscription(): Observable<ISubscription>
    {
        return this.subscription.asObservable();
    }

    public reset(): void
    {
        this.resetGeneration += 1;
        this.subscription.next(subscriptionDefault);
        this.copilots$.next([]);
        this.activeCopilot$.next(null);
        this.copilotsLoaded = false;
        this.sessionCrypto.reset();
    }

    public async fetchSubscription(sessionID: string): Promise<ISubscription>
    {
        const resetGeneration = this.resetGeneration;
        await this.loadCopilots(resetGeneration);
        if (resetGeneration !== this.resetGeneration)
        {
            return this.subscription.getValue();
        }

        const res = await this.sessionCrypto.postAddin<unknown, IR2UserSubscriptionResponse>(
            '/subscription/user-subscription',
            { copilot_id: this.activeCopilot$.getValue()?.id ?? '' },
        );
        if (resetGeneration !== this.resetGeneration)
        {
            return this.subscription.getValue();
        }

        const mapped = this.mapSubscription(res, sessionID);
        this.subscription.next(mapped);
        return mapped;
    }

    private async loadCopilots(resetGeneration: number): Promise<void>
    {
        if (this.copilotsLoaded)
        {
            return;
        }
        try
        {
            const res = await this.sessionCrypto.getAddin<IR2GetCopilotsResponse>(
                '/copilots',
            );
            if (resetGeneration !== this.resetGeneration)
            {
                return;
            }

            const list = res.copilots ?? [];
            this.copilots$.next(list);
            this.copilotsLoaded = true;
            if (list.length > 0 && !this.activeCopilot$.getValue())
            {
                const persistedId = await this.readPersistedCopilotId();
                if (resetGeneration !== this.resetGeneration)
                {
                    return;
                }

                const persisted = persistedId ? list.find(c => c.id === persistedId) : null;
                this.activeCopilot$.next(persisted ?? list[ 0 ]);
            }
        }
        catch (e)
        {
            logger.error('OfficeSubscription', 'loadCopilots', e);
        }
    }

    private async readPersistedCopilotId(): Promise<string | null>
    {
        try
        {
            const setting = await this.chatRepository.readSetting();
            return setting?.copilotId ?? null;
        }
        catch
        {
            return null;
        }
    }

    private mapSubscription(res: IR2UserSubscriptionResponse, sessionID: string): ISubscription
    {
        return {
            success: res.result === 0,
            sessionId: sessionID,
            tenantId: '',
            userId: '',
            userEmail: '',
            userHint: res.user_name ?? '',
            tokensLeft: res.tokens_left ?? 0,
            tokensTill: res.dt_to ? new Date(res.dt_to * 1_000).toISOString() : '',
            subscriptionsCount: res.result === 0 ? 1 : 0,
        };
    }

    public async fetchSubscribeTrial(_sessionID: string): Promise<void>
    {
        logger.warn('fetchSubscribeTrial: not supported on the new backend yet — skipping');
    }
}
